import React, { useId } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { formatInTimeZone } from "date-fns-tz";
import { ChartCard, ChartTooltipFrame, axisTick } from "./ChartCard";
import { tokens } from "../../theme";

/** Points are UTC days (keyed at UTC midnight): label them in UTC, or viewers west of UTC see the day before. */
const formatUtcDay = (ts: number, pattern: string) => formatInTimeZone(ts, "UTC", pattern);

interface IBuybackDataPoint {
  date: number;
  amount: number;
  cumulativeAmount: number;
}

interface BuybacksChartProps {
  data: IBuybackDataPoint[];
  title?: string;
  subtitle?: string;
  sx?: SxProps<Theme>;
}

const TreasuryTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <ChartTooltipFrame minWidth={180}>
      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.5 }}>
        {formatUtcDay(Number(label), "EEE, MMM d, yyyy")}
      </Typography>
      <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>
        {Number(payload[0].value).toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </Typography>
      <Typography sx={{ fontSize: "0.6875rem", color: "text.secondary" }}>Cumulative tokens</Typography>
    </ChartTooltipFrame>
  );
};

const BuybacksChart: React.FC<BuybacksChartProps> = ({
  data,
  title = "Treasury growth",
  subtitle,
  sx,
}) => {
  const lastTs = data.length > 0 ? data[data.length - 1].date : null;
  const gradientId = `treasury-${useId().replace(/:/g, "")}`;

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      sx={[
        { "& .treasury-series .recharts-area-curve": { filter: "drop-shadow(0 0 6px rgba(241, 211, 175, 0.55))" } },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ] as SxProps<Theme>}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={tokens.gold} stopOpacity={0.34} />
              <stop offset="100%" stopColor={tokens.gold} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={tokens.chart.grid} />
          <XAxis
            dataKey="date"
            type="number"
            scale="time"
            domain={["dataMin", "dataMax"]}
            tickFormatter={(t) =>
              t === lastTs ? "Now" : formatUtcDay(t, "MMM d")
            }
            tick={axisTick}
            axisLine={false}
            tickLine={false}
            dy={8}
            interval="preserveStartEnd"
            minTickGap={36}
          />
          <YAxis
            tickFormatter={(t) => Number(t).toLocaleString("en-US", { maximumFractionDigits: 0 })}
            tick={axisTick}
            axisLine={false}
            tickLine={false}
            domain={[0, "auto"]}
            width={46}
          />
          <Tooltip
            content={<TreasuryTooltip />}
            cursor={{ stroke: "rgba(255,255,255,0.22)", strokeWidth: 1 }}
          />
          <Area
            type="monotone"
            dataKey="cumulativeAmount"
            className="treasury-series"
            stroke={tokens.chart.lp}
            strokeWidth={2.25}
            fill={`url(#${gradientId})`}
            name="Cumulative Amount"
            dot={false}
            activeDot={{ r: 5, stroke: "#000", strokeWidth: 2, fill: tokens.chart.lp }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export default BuybacksChart;
